import { Link } from 'react-router-dom';
import { Briefcase, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-6 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to Home</Link>

        <h1 className="text-3xl md:text-4xl tracking-tight font-medium mb-2" style={{fontFamily:'Outfit'}}>Privacy Policy</h1>
        <p className="text-sm text-[#1C2A24]/50 mb-8">Last updated: April 13, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-[#1C2A24]/80 leading-relaxed">
          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>1. Information We Collect</h2>
            <p>We collect information you provide directly and information collected automatically:</p>
            <h3 className="text-base font-medium text-[#1C2A24] mt-4 mb-2">Information You Provide:</h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, password when you register</li>
              <li><strong>Profile Information:</strong> Phone number, location, skills, work experience</li>
              <li><strong>Resume & Documents:</strong> Resumes and files you upload to the Platform</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through Stripe (we do not store card numbers)</li>
              <li><strong>Communications:</strong> Support tickets, community posts, chat messages</li>
              <li><strong>Job Alert Preferences:</strong> Email and category preferences for job notifications</li>
            </ul>
            <h3 className="text-base font-medium text-[#1C2A24] mt-4 mb-2">Information Collected Automatically:</h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Platform</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
              <li><strong>Cookies:</strong> Authentication tokens and preference cookies (see Cookie Policy below)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>To provide, maintain, and improve the Platform</li>
              <li>To process payments and manage subscriptions</li>
              <li>To send job alerts, weekly digests, and account notifications</li>
              <li>To match you with relevant job opportunities</li>
              <li>To enable community features and real-time chat</li>
              <li>To detect and prevent fraud, scams, and abuse</li>
              <li>To respond to support requests</li>
              <li>To analyze usage patterns and improve user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>3. Information Sharing</h2>
            <p>We do not sell your personal information. We may share information with:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Employers:</strong> When you apply for a job, your application details (name, email, resume) are shared with the employer</li>
              <li><strong>Service Providers:</strong> Third-party services that help us operate the Platform:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Stripe (payment processing)</li>
                  <li>SendGrid (email delivery)</li>
                  <li>MongoDB (data storage)</li>
                  <li>Google Analytics (website analytics, if enabled)</li>
                </ul>
              </li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and safety</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>4. Cookie Policy</h2>
            <p>WorkFromJoy uses cookies for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Essential Cookies:</strong> Authentication tokens to keep you logged in (required for the Platform to function)</li>
              <li><strong>Preference Cookies:</strong> Theme settings (light/dark mode)</li>
              <li><strong>Analytics Cookies:</strong> Google Analytics (GA4) to understand how visitors use the site (can be disabled in your browser)</li>
            </ul>
            <p className="mt-2">You can manage cookie preferences through your browser settings. Disabling essential cookies may prevent you from using certain features.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Passwords are hashed using bcrypt (we never store plain-text passwords)</li>
              <li>Payment data is handled by Stripe's PCI-compliant infrastructure</li>
              <li>Data is transmitted over HTTPS encryption</li>
              <li>Access to user data is restricted to authorized personnel</li>
            </ul>
            <p className="mt-2">While we take reasonable precautions, no method of electronic storage is 100% secure. We cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>6. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at support@workfromjoy.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>7. Children's Privacy</h2>
            <p>WorkFromJoy is not intended for users under the age of 16. We do not knowingly collect information from children. If we learn we have collected data from a child under 16, we will delete it promptly.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>8. Data Retention</h2>
            <p>We retain your data for as long as your account is active or as needed to provide services. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or business purposes.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>9. California Residents (CCPA)</h2>
            <p>If you are a California resident, you have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Know what personal information we collect and how it is used</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of the sale of personal information (we do not sell your data)</li>
              <li>Not be discriminated against for exercising your privacy rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through the Platform. Continued use after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-[#1C2A24] mt-8 mb-3" style={{fontFamily:'Outfit'}}>11. Contact Us</h2>
            <p>For privacy-related questions or requests, contact us at:</p>
            <p className="mt-2"><strong>Email:</strong> support@workfromjoy.com</p>
            <p><strong>Website:</strong> <Link to="/support" className="text-[#047857] hover:underline">WorkFromJoy Support</Link></p>
          </section>
        </div>
      </div>
    </div>
  );
}
